/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  Section,
  Container,
  Box,
  Link,
  AtlasCard,
  Text,
  useThemeUI,
  Flex,
} from '@tishman/components';
import { H } from '@hzdg/sectioning';
import React, { useMemo } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import useSize from '@hzdg/use-size';
import invariant from 'invariant';

import { formatStoryCategory } from '../utils';

import type { ComponentPropsWithoutRef } from 'react';

type FeaturedStories = NonNullable<
  Queries.BusinessIndexQuery['meta']
>['featuredStories'];

type BusinessDetailFeaturedStoriesBlockProps = {
  featuredStories: FeaturedStories;
} & ComponentPropsWithoutRef<typeof Section>;

const BusinessDetailFeaturedStoriesBlock = (
  props: BusinessDetailFeaturedStoriesBlockProps,
): JSX.Element => {
  const { dataJson } =
    useStaticQuery<Queries.BusinessDetailFeaturedStoriesQuery>(
      BUSINESS_DETAIL_FEATURED_STORIES_QUERY,
    );

  invariant(dataJson, 'Business JSON data is required!');

  const businessDetailFeaturedStoriesProps = useMemo(() => {
    if (!props.featuredStories) {
      throw new Error(
        'Expected valid Business 65 featured stories sanity data',
      );
    }

    return {
      title: dataJson.featuredStories.title,
      caption: dataJson.featuredStories.caption,
      link: dataJson.featuredStories.link,
      featuredStories: props.featuredStories.map((story) => {
        return {
          title: story?.titleAndSlug?.title ?? '',
          excerpt: story?.excerpt ?? '',
          url: `/magazine/${formatStoryCategory(story?.category ?? '')}/${
            story?.titleAndSlug?.slug?.current
          }`,
          alt: story?.poster?.alt ?? '',
          image: story?.poster?.asset?.gatsbyImageData,
          category: story?.category ?? '',
          formattedPublishAt: story?.formattedPublishAt ?? '',
        };
      }),
    };
  }, [props, dataJson]);

  const [{ width: sectionWidth }, sectionRef] = useSize();
  const { theme } = useThemeUI();
  const tabletBreakpoint = parseInt(theme?.breakpoints?.[1] || '832px');
  const useHoverAnimation = sectionWidth > tabletBreakpoint;
  return (
    businessDetailFeaturedStoriesProps && (
      <Section
        data-section="BusinessDetailFeaturedStories"
        sx={{ py: 8 }}
        {...props}
        ref={sectionRef}
      >
        <Container>
          <Box sx={{ textAlign: 'center' }}>
            <H
              sx={{
                variant: 'styles.h1',
                mb: 3,
                fontFamily: 'headingSecondary',
                color: 'accent',
              }}
            >
              {businessDetailFeaturedStoriesProps.title}
            </H>
            <Text
              as="p"
              sx={{
                variant: 'text.mediumP',
                mb: 3,
                color: 'accent',
              }}
            >
              {businessDetailFeaturedStoriesProps.caption}
            </Text>
            <Link
              href={businessDetailFeaturedStoriesProps.link.url}
              sx={{
                'color': 'accent',
                '::after': { backgroundColor: 'accent' },
              }}
              variant="underline"
            >
              {businessDetailFeaturedStoriesProps.link.label}
            </Link>
          </Box>
          {businessDetailFeaturedStoriesProps.featuredStories.length ? (
            <Flex
              sx={{
                flexWrap: 'wrap',
                justifyContent: 'center',
                mt: 4,
              }}
            >
              {businessDetailFeaturedStoriesProps.featuredStories.map(
                (story) => (
                  <Box
                    key={story.url}
                    sx={{
                      flex: '0 0 auto',
                      maxWidth: '100%',
                      width: ['100%', null, 'calc(100% / 3)'],
                    }}
                  >
                    <AtlasCard
                      {...story}
                      sx={{ width: '100%' }}
                      useHoverAnimation={useHoverAnimation}
                    />
                  </Box>
                ),
              )}
            </Flex>
          ) : null}
        </Container>
      </Section>
    )
  );
};

export default BusinessDetailFeaturedStoriesBlock;

const BUSINESS_DETAIL_FEATURED_STORIES_QUERY = graphql`
  query BusinessDetailFeaturedStories {
    dataJson(jsonId: { eq: "business-detail" }) {
      featuredStories {
        caption
        title
        link {
          label
          url
        }
      }
    }
  }
`;
